import {
  cleanCardSettings,
  DEFAULT_CHECKOUT_APPEARANCE,
  DEFAULT_PAYMENT_OPTIONS,
  HEX_COLOR_RE,
  validateCardSettings,
  type CardCheckoutSettings,
  type CheckoutAppearanceValue,
  type CountryAccessValue,
  type PaymentOptionsValue,
} from '../../components/merchant-config';

export interface BusinessStep {
  name: string;
  legal_name: string;
  email: string;
  notification_email: string;
  phone_number: string;
  country: string;
  registration_number: string;
  tax_id: string;
  is_active: boolean;
  logo: File | null;
}

export interface IntegrationStep {
  webhook_url: string;
  webhook_secret: string;
  email_from_name: string;
  developer_can_view_transactions: boolean;
  metadata: Array<{ key: string; value: string }>;
}

export interface MerchantWizardState {
  business: BusinessStep;
  appearance: CheckoutAppearanceValue;
  payments: PaymentOptionsValue;
  card: CardCheckoutSettings;
  countries: CountryAccessValue;
  integration: IntegrationStep;
}

export type StepId = 'business' | 'checkout' | 'card' | 'countries' | 'integration' | 'review';

export const STEPS: ReadonlyArray<{ id: StepId; label: string }> = [
  { id: 'business', label: 'Business' },
  { id: 'checkout', label: 'Checkout & payments' },
  { id: 'card', label: 'Card checkout' },
  { id: 'countries', label: 'Countries' },
  { id: 'integration', label: 'Integration' },
  { id: 'review', label: 'Review' },
];

export function initialState(): MerchantWizardState {
  return {
    business: {
      name: '', legal_name: '', email: '', notification_email: '', phone_number: '', country: '',
      registration_number: '', tax_id: '', is_active: true, logo: null,
    },
    appearance: { ...DEFAULT_CHECKOUT_APPEARANCE },
    payments: { ...DEFAULT_PAYMENT_OPTIONS },
    card: {},
    countries: { allowed: [], disallowed: [] },
    integration: {
      webhook_url: '', webhook_secret: '', email_from_name: '',
      developer_can_view_transactions: false, metadata: [],
    },
  };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Step-local validation; the server stays authoritative. Returns field → message. */
export function validateStep(id: StepId, s: MerchantWizardState): Record<string, string> {
  const e: Record<string, string> = {};
  if (id === 'business') {
    if (!s.business.name.trim()) e['name'] = 'Required.';
    if (!EMAIL_RE.test(s.business.email.trim())) e['email'] = 'Enter a valid email.';
    const notify = s.business.notification_email.trim();
    if (notify && !EMAIL_RE.test(notify)) e['notification_email'] = 'Enter a valid email.';
    if (!s.business.country) e['country'] = 'Required.';
  } else if (id === 'checkout') {
    for (const key of ['checkout_primary_color', 'checkout_secondary_color', 'checkout_accent_color', 'checkout_background_color'] as const) {
      if (!HEX_COLOR_RE.test(s.appearance[key])) e[key] = 'Use a #RRGGBB colour.';
    }
  } else if (id === 'card') {
    const problems = validateCardSettings(s.card);
    if (problems.length) e['card_checkout_settings'] = problems.join(' ');
  } else if (id === 'integration') {
    if (s.integration.webhook_url && !/^https:\/\//i.test(s.integration.webhook_url.trim())) {
      e['webhook_url'] = 'Must start with https://';
    }
    const keys = s.integration.metadata.map((r) => r.key.trim()).filter(Boolean);
    if (new Set(keys).size !== keys.length) e['metadata'] = 'Duplicate keys.';
  }
  return e;
}

/** Request body for `POST auth/merchants/`: empty optional values are omitted. */
export function buildPayload(s: MerchantWizardState): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: s.business.name.trim(),
    email: s.business.email.trim(),
    ...(s.business.notification_email.trim() ? { notification_email: s.business.notification_email.trim() } : {}),
    country: s.business.country,
    is_active: s.business.is_active,
    ...s.appearance,
    ...s.payments,
    developer_can_view_transactions: s.integration.developer_can_view_transactions,
    card_checkout_settings: cleanCardSettings(s.card),
    allowed_countries: s.countries.allowed,
    disallowed_countries: s.countries.disallowed,
  };
  const optional: Record<string, string> = {
    legal_name: s.business.legal_name,
    phone_number: s.business.phone_number,
    registration_number: s.business.registration_number,
    tax_id: s.business.tax_id,
    webhook_url: s.integration.webhook_url,
    webhook_secret: s.integration.webhook_secret,
    email_from_name: s.integration.email_from_name,
  };
  for (const [key, value] of Object.entries(optional)) {
    if (value.trim()) body[key] = value.trim();
  }
  if (!s.appearance.checkout_custom_css.trim()) delete body['checkout_custom_css'];
  const metadata = Object.fromEntries(
    s.integration.metadata.filter((r) => r.key.trim()).map((r) => [r.key.trim(), r.value]),
  );
  body['metadata'] = metadata;
  if (s.business.logo) body['logo'] = s.business.logo;
  return body;
}

/** Which wizard step owns each API field, for mapping 400 errors back. */
export const FIELD_STEP: Record<string, StepId> = {
  name: 'business', legal_name: 'business', email: 'business', notification_email: 'business', phone_number: 'business',
  country: 'business', registration_number: 'business', tax_id: 'business', logo: 'business',
  is_active: 'business',
  checkout_primary_color: 'checkout', checkout_secondary_color: 'checkout',
  checkout_accent_color: 'checkout', checkout_background_color: 'checkout',
  checkout_default_locale: 'checkout', checkout_custom_css: 'checkout', fee_bearer: 'checkout',
  allow_merchant_fees: 'checkout', checkout_card_enabled: 'checkout',
  checkout_mainmoney_enabled: 'checkout', payment_link_disabled_methods: 'checkout',
  card_checkout_settings: 'card',
  allowed_countries: 'countries', disallowed_countries: 'countries', non_field_errors: 'countries',
  webhook_url: 'integration', webhook_secret: 'integration', email_from_name: 'integration',
  developer_can_view_transactions: 'integration', metadata: 'integration',
};

/** Split a DRF 400 body into per-field messages (strings only; raw exceptions are never shown). */
export function fieldErrors(body: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!body || typeof body !== 'object') return out;
  for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
    const text = Array.isArray(value) ? value.map(String).join(' ') : typeof value === 'string' ? value : '';
    if (text) out[key] = text;
  }
  return out;
}
